import React, { useState, useEffect } from 'react';
import { C, S } from '../utils/styles';
import Icon from './Icon';
import StyledSelect from './StyledSelect';

// Deal scenario presets
const DEAL_SCENARIOS = {
  maximum: {
    label: 'Maximum Eligibility',
    description: 'Widest net: include as many employees as legally possible',
    settings: {
      waitingPeriod: 0,
      minWeeklyHours: 0,
      eligibleClasses: ['fulltime_w2', 'parttime_w2', 'seasonal_w2', 'variable_hour_w2'],
      availablePlanTiers: ['Plan 1500', 'Plan 1200', 'Plan 1050', 'Plan 900', 'Plan 750', 'Plan 600'],
    },
  },
  conservative: {
    label: 'Conservative',
    description: 'Industry standard: FT/PT only, 60-day wait, 30 hrs/week',
    settings: {
      waitingPeriod: 60,
      minWeeklyHours: 30,
      eligibleClasses: ['fulltime_w2', 'parttime_w2'],
      availablePlanTiers: ['Plan 1200', 'Plan 900', 'Plan 750', 'Plan 600'],
    },
  },
  standard: {
    label: 'Standard',
    description: 'Balanced approach: FT/PT, 30-day wait, 20 hrs/week',
    settings: {
      waitingPeriod: 30,
      minWeeklyHours: 20,
      eligibleClasses: ['fulltime_w2', 'parttime_w2'],
      availablePlanTiers: ['Plan 1200', 'Plan 900', 'Plan 750', 'Plan 600'],
    },
  },
};

const ELIGIBLE_CLASS_OPTIONS = [
  { value: 'fulltime_w2', label: 'Full-Time W-2' },
  { value: 'parttime_w2', label: 'Part-Time W-2' },
  { value: 'seasonal_w2', label: 'Seasonal W-2' },
  { value: 'variable_hour_w2', label: 'Variable Hour W-2' },
  { value: 'contractor_1099', label: '1099 Contractors' },
];

const PLAN_TIER_OPTIONS = [
  { value: 'Plan 1500', label: 'Plan 1500 ($125/mo)' },
  { value: 'Plan 1200', label: 'Plan 1200 ($100/mo)' },
  { value: 'Plan 1050', label: 'Plan 1050 ($87.50/mo)' },
  { value: 'Plan 900', label: 'Plan 900 ($75/mo)' },
  { value: 'Plan 750', label: 'Plan 750 ($62.50/mo)' },
  { value: 'Plan 600', label: 'Plan 600 ($50/mo)' },
];

const PAY_FREQUENCY_OPTIONS = [
  { value: 12, label: 'Monthly (12)' },
  { value: 24, label: 'Semi-monthly (24)' },
  { value: 26, label: 'Biweekly (26)' },
  { value: 52, label: 'Weekly (52)' },
];

const OPTIMIZATION_OPTIONS = [
  { value: 'employee_benefit', label: 'Employee Benefit (maximize employee take-home)' },
  { value: 'employer_savings', label: 'Employer Savings (maximize FICA savings)' },
  { value: 'balanced', label: 'Balanced (equal weight on both)' },
];

export default function AnalysisConfigEditor({ config, onSave, onCancel, systemDefaults }) {
  // Convert eligibleClasses from object to array format if needed
  const convertEligibleClassesToArray = (classes) => {
    if (Array.isArray(classes)) return classes;
    if (typeof classes === 'object' && classes !== null) {
      const result = [];
      if (classes.fulltime_w2) result.push('fulltime_w2');
      if (classes.parttime_w2) result.push('parttime_w2');
      if (classes.seasonal_temp || classes.seasonal_w2) result.push('seasonal_w2');
      if (classes.variable_hour_w2) result.push('variable_hour_w2');
      if (classes.union_cba) result.push('union_cba');
      if (classes.contractor_1099) result.push('contractor_1099');
      return result;
    }
    return ['fulltime_w2', 'parttime_w2'];
  };
  
  // Extract system defaults from the config object
  const sysDefaults = {
    waitingPeriod: systemDefaults?.config?.employerPlanRules?.waiting_period_days ?? 60,
    minWeeklyHours: systemDefaults?.config?.employerPlanRules?.minimum_weekly_hours ?? 20,
    eligibleClasses: systemDefaults?.config?.employerPlanRules?.eligible_classes || {
      fulltime_w2: true,
      parttime_w2: true,
      seasonal_temp: false,
      union_cba: false,
    },
    localMinWage: systemDefaults?.config?.employerPlanRules?.state_local_min_wage_override ?? 7.25,
    availablePlanTiers: systemDefaults?.config?.planTiers?.map(t => t.label) || ['Plan 1500', 'Plan 1200', 'Plan 1050', 'Plan 900', 'Plan 750', 'Plan 600'],
    payFrequency: systemDefaults?.config?.payrollConstants?.defaultPayFrequency ?? 26,
  };
  
  const [overrides, setOverrides] = useState({
    waitingPeriod: config?.overrides?.waitingPeriod ?? sysDefaults.waitingPeriod,
    minWeeklyHours: config?.overrides?.minWeeklyHours ?? sysDefaults.minWeeklyHours,
    eligibleClasses: config?.overrides?.eligibleClasses 
      ? convertEligibleClassesToArray(config.overrides.eligibleClasses) 
      : convertEligibleClassesToArray(sysDefaults.eligibleClasses),
    localMinWage: config?.overrides?.localMinWage ?? sysDefaults.localMinWage,
    availablePlanTiers: config?.overrides?.availablePlanTiers ?? sysDefaults.availablePlanTiers,
  });

  const [controls, setControls] = useState({
    payFrequency: config?.controls?.payFrequency ?? sysDefaults.payFrequency,
    optimizationStrategy: config?.controls?.optimizationStrategy ?? 'employee_benefit',
    minEmployeeNet: config?.controls?.minEmployeeNet ?? 0,
    includeFuta: config?.controls?.includeFuta ?? false,
  });

  const applyScenario = (scenarioKey) => {
    const scenario = DEAL_SCENARIOS[scenarioKey];
    setOverrides(prev => ({
      ...prev,
      ...scenario.settings,
    }));
  };

  const handleSave = () => {
    // Convert eligibleClasses array back to object format
    const eligibleClassesObject = {
      fulltime_w2: overrides.eligibleClasses.includes('fulltime_w2'),
      parttime_w2: overrides.eligibleClasses.includes('parttime_w2'),
      seasonal_temp: overrides.eligibleClasses.includes('seasonal_w2'),
      union_cba: overrides.eligibleClasses.includes('union_cba'),
    };
    
    onSave({
      overrides: {
        ...overrides,
        eligibleClasses: eligibleClassesObject,
      },
      controls,
    });
  };

  const toggleClass = (classValue) => {
    setOverrides(prev => ({
      ...prev,
      eligibleClasses: prev.eligibleClasses.includes(classValue)
        ? prev.eligibleClasses.filter(c => c !== classValue)
        : [...prev.eligibleClasses, classValue],
    }));
  };

  const togglePlan = (planValue) => {
    setOverrides(prev => ({
      ...prev,
      availablePlanTiers: prev.availablePlanTiers.includes(planValue)
        ? prev.availablePlanTiers.filter(p => p !== planValue)
        : [...prev.availablePlanTiers, planValue],
    }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Deal Scenario Presets */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10, textTransform: 'uppercase' }}>
          Quick Presets
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {Object.entries(DEAL_SCENARIOS).map(([key, scenario]) => (
            <button
              key={key}
              onClick={() => applyScenario(key)}
              style={{
                flex: 1,
                padding: '10px 12px',
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                background: C.bg,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = C.accent}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = C.border}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                {scenario.label}
              </div>
              <div style={{ fontSize: 10, color: C.textMid, lineHeight: 1.4 }}>
                {scenario.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Employer Overrides */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 12 }}>
          Employer Overrides
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Waiting Period */}
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Waiting Period</label>
            <StyledSelect
              value={String(overrides.waitingPeriod)}
              onValueChange={(val) => setOverrides(prev => ({ ...prev, waitingPeriod: parseInt(val) }))}
              options={[
                { value: "0", label: "No waiting period" },
                { value: "30", label: "30 days" },
                { value: "60", label: "60 days" },
                { value: "90", label: "90 days" }
              ]}
              style={{ fontSize: 13 }}
            />
          </div>

          {/* Min Weekly Hours */}
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Min Weekly Hours</label>
            <input
              type="number"
              value={overrides.minWeeklyHours}
              onChange={(e) => setOverrides(prev => ({ ...prev, minWeeklyHours: parseInt(e.target.value) || 0 }))}
              min="0"
              max="40"
              style={{ ...S.input, fontSize: 13 }}
            />
          </div>
        </div>

        {/* Eligible Classes */}
        <div style={{ marginTop: 16 }}>
          <label style={{ ...S.label, marginBottom: 8, display: 'block' }}>Eligible Employee Classes</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {ELIGIBLE_CLASS_OPTIONS.map(option => (
              <label
                key={option.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 8,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                  cursor: 'pointer',
                  background: overrides.eligibleClasses.includes(option.value) ? C.accentBg : C.bg,
                }}
              >
                <input
                  type="checkbox"
                  checked={overrides.eligibleClasses.includes(option.value)}
                  onChange={() => toggleClass(option.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: 12, fontWeight: 500, color: C.text }}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Local Min Wage */}
        <div style={{ marginTop: 16 }}>
          <label style={{ ...S.label, marginBottom: 6 }}>Local Minimum Wage ($/hr)</label>
          <input
            type="number"
            value={overrides.localMinWage}
            onChange={(e) => setOverrides(prev => ({ ...prev, localMinWage: parseFloat(e.target.value) || 0 }))}
            step="0.25"
            min="0"
            style={{ ...S.input, fontSize: 13, width: 200 }}
          />
        </div>

        {/* Available Plan Tiers */}
        <div style={{ marginTop: 16 }}>
          <label style={{ ...S.label, marginBottom: 8, display: 'block' }}>Available Plan Tiers</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {PLAN_TIER_OPTIONS.map(option => (
              <label
                key={option.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 8,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                  cursor: 'pointer',
                  background: overrides.availablePlanTiers.includes(option.value) ? C.greenLight : C.bg,
                }}
              >
                <input
                  type="checkbox"
                  checked={overrides.availablePlanTiers.includes(option.value)}
                  onChange={() => togglePlan(option.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Analysis Controls */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 12 }}>
          Analysis Controls
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Pay Frequency */}
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Pay Frequency</label>
            <StyledSelect
              value={String(controls.payFrequency)}
              onValueChange={(val) => setControls(prev => ({ ...prev, payFrequency: parseInt(val) }))}
              options={PAY_FREQUENCY_OPTIONS.map(opt => ({ value: String(opt.value), label: opt.label }))}
              style={{ fontSize: 13 }}
            />
          </div>

          {/* Min Employee Net */}
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Min Employee Net Benefit ($)</label>
            <input
              type="number"
              value={controls.minEmployeeNet}
              onChange={(e) => setControls(prev => ({ ...prev, minEmployeeNet: parseFloat(e.target.value) || 0 }))}
              min="0"
              step="10"
              style={{ ...S.input, fontSize: 13 }}
            />
          </div>
        </div>

        {/* Optimization Strategy */}
        <div style={{ marginTop: 16 }}>
          <label style={{ ...S.label, marginBottom: 8, display: 'block' }}>Optimization Strategy</label>
          {OPTIMIZATION_OPTIONS.map(option => (
            <label
              key={option.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: 10,
                marginBottom: 8,
                border: `1px solid ${controls.optimizationStrategy === option.value ? C.accent : C.border}`,
                borderRadius: 6,
                cursor: 'pointer',
                background: controls.optimizationStrategy === option.value ? C.accentBg : C.bg,
              }}
            >
              <input
                type="radio"
                name="optimization"
                value={option.value}
                checked={controls.optimizationStrategy === option.value}
                onChange={(e) => setControls(prev => ({ ...prev, optimizationStrategy: e.target.value }))}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                {option.label}
              </span>
            </label>
          ))}
        </div>

        {/* Include FUTA */}
        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={controls.includeFuta}
              onChange={(e) => setControls(prev => ({ ...prev, includeFuta: e.target.checked }))}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
              Include FUTA in savings calculation
            </span>
          </label>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '10px',
            background: 'transparent',
            color: C.textMid,
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          style={{
            flex: 1,
            padding: '10px',
            background: C.accent,
            color: 'white',
            border: 'none',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Icon name="save-outline" style={{ fontSize: 14 }} />
          Save Configuration
        </button>
      </div>
    </div>
  );
}
